import { ApiService } from './api.service';
import { AnalyticsService } from './analytics.service';

/**
 * Everything QuoteRequestService.requestQuote() needs from the calling component. `apiService`
 * and `analytics` are passed in (rather than injected here) because both OrderComponent and
 * InlineQuotationComponent hold their OWN component-level ApiService instance (`apiService` is
 * private on OrderComponent — see CLAUDE.md "Services" section), so there is no single instance
 * this service could inject for both callers.
 */
export interface QuoteRequestContext {
  apiService: ApiService;
  analytics: AnalyticsService;
  /** OrderComponent.buildOrder() — builds `this.order` from the raw form values. */
  buildOrder: (values: any) => void;
  /** OrderComponent.saveOrder() — POST/PATCH `this.order`, resolves once `orderId` is set. */
  saveOrder: () => Promise<any>;
  /** OrderComponent.saveState() — persist form values + orderId to local storage. */
  saveState: () => void;
  /** Current OrderComponent.orderId (read AFTER saveOrder() resolves). */
  getOrderId: () => string;
  /** OrderComponent.analyticsContext() — non-PII context merged into umami events. */
  analyticsContext: (extra?: Record<string, any>) => Record<string, any>;
}

export interface QuoteRequestOutcome {
  success: boolean;
  /** Which step failed, when `success` is false. */
  stage?: 'save' | 'request';
  /** Server-provided error message, if any (caller decides whether/how to show it). */
  errorMessage?: string;
}

/**
 * The `buildOrder -> saveOrder -> POST /orders/{id}/requestQuote` sequence shared by the full
 * booking form (OrderComponent.requestQuote(), triggered from the product/summary quote dialogs)
 * and the inline-quotation widget (InlineQuotationComponent.submitQuotation()). Both used to carry
 * their own copy of this sequence — this is the single place it now lives.
 *
 * Deliberately a plain class with STATIC methods, not an `@Injectable` — OrderComponent's
 * constructor is inherited through a 4-level chain (OrderComponent -> InlineWebbookerComponent ->
 * PopupWidgetComponent -> MobilePopupWidgetComponent, plus InlineWebbookerComponent ->
 * InlineQuotationComponent) where CLAUDE.md flags that every added constructor parameter must be
 * threaded through every subclass's constructor + super() call. This service needs no state and
 * no dependencies of its own (its two collaborators are passed in via `QuoteRequestContext`), so
 * there is nothing to inject and no reason to touch that chain.
 *
 * What stays caller-side (differs per caller, deliberately not consolidated here):
 *  - Form validation before calling (the wizard's per-page `checkPageValidity` vs the merged
 *    single-page form's two-part validation) and the `email` requirement.
 *  - What happens on success (open the `quoteRequestedModal` vs set `submitted = true` +
 *    build the inline confirmation) and on failure (alert vs inline message).
 */
export class QuoteRequestService {

  static requestQuote(ctx: QuoteRequestContext, values: any): Promise<QuoteRequestOutcome> {
    ctx.buildOrder(values);
    ctx.saveState();

    return ctx.saveOrder().then(() => {
      const orderId = ctx.getOrderId();
      if (!orderId) {
        return { success: false, stage: 'save' } as QuoteRequestOutcome;
      }
      return ctx.apiService.postCustom(`/orders/${orderId}/requestQuote`, {}).toPromise().then(() => {
        ctx.analytics.umamiEvent('quote_requested', ctx.analyticsContext());
        return { success: true } as QuoteRequestOutcome;
      }, (error) => {
        const reason = (error && error.error && error.error.error && error.error.error.message) || 'request_error';
        ctx.analytics.umamiEvent('quote_failed', ctx.analyticsContext({ stage: 'request', reason }));
        const errorMessage = (error && error.error && error.error.error && error.error.error.message)
          || (error && error.error && error.error.message);
        return { success: false, stage: 'request', errorMessage } as QuoteRequestOutcome;
      });
    }, (error) => {
      const errorMessage = error && error.error && error.error.message;
      ctx.analytics.umamiEvent('quote_failed', ctx.analyticsContext({ stage: 'save', reason: errorMessage || 'save_error' }));
      return { success: false, stage: 'save', errorMessage } as QuoteRequestOutcome;
    });
  }

  /**
   * Whether the "Request a quote" action should be offered at all.
   *
   * The company-level switch (`Company.settings.rideProcessing.quoteFlow.enabled`) has been
   * retired. The only gate is a per-form opt-in: `Form.config.quotations.enabled === true`
   * turns it on for one form. A missing setting means no quotation button.
   */
  static isQuotationEnabled(formConfig: any): boolean {
    const form = formConfig || {};
    return !!(form.quotations && form.quotations.enabled === true);
  }
}
